import { defineStore, acceptHMRUpdate } from 'pinia';
import { getSegmentRecommendations as fetchSegmentRecommendations } from '@/vuex/modules/smart-segments/smart-segments.utils';
import { LOADING_STATES } from '@/constants';
import { formatMetadataForRules } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { useMetadataStore } from '@/stores/metadata.store';
import get from 'lodash/get';

const { LOADING, RESOLVED, EMPTY, REJECTED } = LOADING_STATES;
const MIN_BASE_VISITORS_COUNT = 100;

function getInitialState () {
    return {
        status: null as string | null,
        map: {} as Record<string, unknown[]>,
    };
}

export const useSmartSegmentsStore = defineStore('smartSegments', {
    state: getInitialState,
    getters: {
        currentRecommendedRules (): unknown[] {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const { useJourneysStore } = require('@/stores/journeys.store'); // TODO APP-157977: journeys migrated to Pinia
            const journeysStore = useJourneysStore();
            const journey = journeysStore.getActiveJourney;
            const journeySegmentId = get(journey, 'audienceUiHint.filters[0].segmentId', 'custom');
            const segmentId = journeySegmentId === 'noOne' ? 'everyone' : journeySegmentId;
            const { itemId } = journeysStore.getGoal;

            return this.map[`${itemId}|${segmentId}`] || [];
        },
        segmentsFromRecommendedRules () {
            const { metadataList } = useMetadataStore();

            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const { useJourneysStore } = require('@/stores/journeys.store'); // TODO APP-157977: journeys migrated to Pinia
            const journey = useJourneysStore().getActiveJourney;
            const baseSegmentId = get(journey, 'audienceUiHint.filters[0].segmentId', 'custom');

            const formatedMetadataForRules = formatMetadataForRules(metadataList);

            return (this.currentRecommendedRules as unknown as Array<{ key: string; op: string; value: unknown }>).map(({ key, op, value }) => {
                const formatedMetadataForRule = formatedMetadataForRules.find((rule: Record<string, unknown>) => (rule.field as string).includes(key));

                const recommendedRule = {
                    ...formatedMetadataForRule,
                    operator: op,
                    value,
                };

                let newSegmentfilters;
                if (baseSegmentId !== 'custom') {
                    const { useSegmentsStore } = require('@/stores/segments.store'); // eslint-disable-line @typescript-eslint/no-require-imports
                    const baseSegment = useSegmentsStore().segmentById(baseSegmentId);
                    newSegmentfilters = [...get(baseSegment, 'definition.filters', []), recommendedRule];
                } else {
                    newSegmentfilters = [...journey.audienceUiHint.filters, recommendedRule];
                }

                const definition = { filters: newSegmentfilters };
                const name = `<${value}> segment recommendation`;

                return { definition, name, shared: true };
            });
        },
    },
    actions: {
        addOrUpdateSegmentsInMap ({ targetId, segmentId, recommendedSegments = [] }: { targetId: string; segmentId: string; recommendedSegments?: unknown[] }) {
            this.map[`${targetId}|${segmentId}`] = recommendedSegments;
        },
        setStatus (newStatus: string | null) {
            this.status = newStatus;
        },
        async getSegmentRecommendations (
            { appId, itemId, itemType, segmentId = 'custom', audience = [], baseVisitorsCount }: {
                appId: unknown;
                itemId: string;
                itemType: string;
                segmentId?: string;
                audience?: unknown[];
                baseVisitorsCount: number;
            },
        ) {
            if (baseVisitorsCount < MIN_BASE_VISITORS_COUNT) {
                this.setStatus(EMPTY);

                return;
            }

            const smartSegmentKey = `${itemId}|${segmentId}`;

            if (this.map[smartSegmentKey]) {
                this.setStatus(RESOLVED);

                return;
            }

            try {
                this.setStatus(LOADING);
                const { smartSegments } = await fetchSegmentRecommendations(
                    appId,
                    itemId,
                    itemType,
                    audience,
                    baseVisitorsCount,
                );

                this.setStatus(smartSegments.length > 0 ? RESOLVED : EMPTY);

                if (smartSegments.length > 0) {
                    this.addOrUpdateSegmentsInMap({
                        targetId: itemId,
                        segmentId,
                        recommendedSegments: smartSegments,
                    });
                }
            } catch (error) {
                this.setStatus(REJECTED);
                throw new Error(`Failed to fetch segment recommendations: ${error}`);
            }
        },
        setEligibleVisitorsForRecommendedSegments (eligibleVisitorsForRecommendations: Array<Record<string, number>>) {
            (this.currentRecommendedRules as unknown as Record<string, unknown>[]).forEach((recommendation, index) => {
                const fieldName = `eligibleVisitorCount${index + 1}`;
                const eligibleVisitorsForRecommendation = eligibleVisitorsForRecommendations.find(
                    (rule) => rule[fieldName],
                );
                recommendation.eligibleVisitors = eligibleVisitorsForRecommendation?.[fieldName];
            });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSmartSegmentsStore, module.hot));
}
